import { ApiGridParams } from '@interfaces/central-monitoring';
import { GridType, ObservationType } from '@interfaces-clean/observations.interface';

const subjectReviewLogUrl = 'SubjectMonitoring/SubjectReviewLogs';
const subjectObservationsUrl = 'SubjectMonitoring/SubjectObservations';
const subjectActionsUrl = 'SubjectMonitoring/SubjectActions';

function observationTypeToUrl(observationType: ObservationType): string {
    switch (observationType) {
        case ObservationType.Site:
            return 'Site';
        case ObservationType.Study:
            return 'Study';
        case ObservationType.Controls:
            return 'Controls';
        default:
            throw new Error(`Observation type not supported: ${observationType}`);
    }
}

function reviewLogTypeToUrl(observationType: ObservationType): string {
    switch (observationType) {
        case ObservationType.Site:
            return 'SiteReviewLog';
        case ObservationType.Study:
            return 'StudyReviewLog';
        case ObservationType.Controls:
            return 'ControlsReviewLog';
        default:
            throw new Error(`Observation type not supported for review log: ${observationType}`);
    }
}

export const formDataUrl = (args: ApiGridParams) => {
    const { studyRef } = args;
    let url;
    let level;
    let observationId;
    let reviewLogId;
    let actionChild;
    let actionId;

    switch (args.grid) {
        case 'SubjectReviewActivityLog':
            if (!!studyRef && !!args.reviewId)
                return `SubjectMonitoring/SubjectReviewActivityLogGrid/Data/${studyRef}/${args.reviewId}`;
            return null;
        case 'SmReviewLog':
            ({ observationId } = args);
            if (!!studyRef && !observationId) return `${subjectReviewLogUrl}/Grid/Data/${studyRef}`;
            if (!!studyRef && !!observationId) return `${subjectReviewLogUrl}/Grid/Data/${studyRef}/${observationId}`;
            return null;
        case 'SmObservations':
            ({ observationId } = args);
            if (!!studyRef && !observationId) return `${subjectObservationsUrl}/Grid/Data/${studyRef}`;
            if (!!studyRef && !!observationId)
                return `${subjectObservationsUrl}/Grid/Data/${studyRef}/${observationId}`;
            return null;
        case 'SmActions':
            ({ observationId } = args);
            if (!!studyRef && !observationId) return `${subjectActionsUrl}/Grid/Data/${studyRef}`;
            if (!!studyRef && !!observationId) return `${subjectActionsUrl}/Grid/Data/${studyRef}/${observationId}`;
            return null;
        case 'ReviewLog':
            ({ level, observationId, actionChild, actionId } = args);
            url = reviewLogTypeToUrl(level);

            // For Study, Site and Control and Review Log child grids - we don't use the observation id (get all review logs
            // that belong to the specified observation) but instead we use the review log id associated to the
            // action selected in the parent grid
            if (actionChild === true && !!studyRef && !!actionId) {
                return `CentralMonitoring/${url}/Grid/${studyRef}/ActionChild/${actionId}`;
            }
            if (studyRef && !observationId) {
                return `CentralMonitoring/${url}/Grid/${studyRef}`;
            }
            if (!!studyRef && !!observationId) return `CentralMonitoring/${url}/Grid/${studyRef}/${observationId}`;
            return null;
        case 'Actions':
            ({ level, observationId, reviewLogId } = args);

            // For the Control Review Log Actions Child grid (only!) we don't use the observation id (get all actions
            // that belong to the specified observation) but instead we use the selected review log id for the parent
            // grid (get all actions for the selected review log).
            //
            // We put both values in observationId because it's easier but observationId isn't always an observationId.
            //
            // This will change in a later release so all action child grids show actions for the selected review log.
            if (!!studyRef && !!reviewLogId)
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/${studyRef}/ForReviewLog/${reviewLogId}`;

            if (studyRef && !observationId) {
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/${studyRef}`;
            }
            if (!!studyRef && !!observationId)
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/${studyRef}/${observationId}`;
            return null;
        case 'Observations':
            ({ level, observationId } = args);
            if (!!studyRef && !observationId)
                return `CentralMonitoring/Observation/${observationTypeToUrl(level)}/Grid/${studyRef}`;
            if (!!studyRef && !!observationId)
                return `CentralMonitoring/Observation/${observationTypeToUrl(level)}/Grid/${studyRef}/${observationId}`;
            return null;
        case 'DataTable': {
            const {
                dataset,
                datatable,
                subjectId,
                isSubjectLevelTable,
                comparisonDatasetId,
                dataViewRef,
                subjectsOnReviewId,
                subjectIsOnAReview,
            } = {
                ...args,
            };
            if (!!studyRef && !!dataset && !!datatable) {
                url = `CentralMonitoring/Grid/DataTable/${studyRef}/${dataset}/${datatable}`;
                if (!!dataViewRef) url += `?dataViewRef=${dataViewRef}`;
                return url;
            }
            if (!!studyRef && !!dataset && !!comparisonDatasetId) {
                // Get subjects on or off a specific review
                const queryParams = new URLSearchParams();
                if (subjectIsOnAReview != null && subjectsOnReviewId != null) {
                    queryParams.append('subjectIsOnAReview', `${subjectIsOnAReview}`);
                    queryParams.append('subjectsOnReviewId', `${subjectsOnReviewId}`);
                }
                return `SubjectMonitoring/OverviewGridData/${studyRef}/${dataset}/${comparisonDatasetId}?${queryParams}`;
            }
            if (!!studyRef && !!dataset) {
                // Get subjects on or off a specific review
                const queryParams = new URLSearchParams();
                if (subjectIsOnAReview != null && subjectsOnReviewId != null) {
                    queryParams.append('subjectIsOnAReview', `${subjectIsOnAReview}`);
                    queryParams.append('subjectsOnReviewId', `${subjectsOnReviewId}`);
                }
                return `SubjectMonitoring/OverviewGridData/${studyRef}/${dataset}?${queryParams}`;
            }
            return null;
        }

        case 'SMDataTable': {
            const { dataset, datatable, subjectId, isSubjectLevelTable, reviewRecordId } = args;

            const queryParams = new URLSearchParams();
            if (reviewRecordId != null) {
                queryParams.append('reviewRecordId', `${reviewRecordId}`);
            }

            return `SubjectMonitoring/DataListings/${studyRef}/${dataset}/${datatable}/${subjectId}/${isSubjectLevelTable}?${queryParams}`;
        }

        case 'ControlsHubOoTGrid':
            url = `CentralMonitoring/ControlsHub/${args.studyRef}/outOfToleranceGridData`;
            if (args.country) url += `?country=${args.country}`;
            return url;
        case 'ControlsHubPanelGrid':
            url = `CentralMonitoring/ControlsHub/${args.studyRef}/${args.datasetId}/panelGridData/${args.panelRef}`;
            if (args.country) url += `?country=${args.country}`;
            return url;
        default:
            return null;
    }
};
export const formIndexUrl = (args: ApiGridParams) => {
    const { studyRef } = args;
    let level;
    let observationId;
    let reviewLogId;
    let url;

    switch (args.grid) {
        case 'ReviewLog':
            ({ level, observationId } = args);
            url = reviewLogTypeToUrl(level);
            if (studyRef && !observationId) {
                return `CentralMonitoring/${url}/ItemIndex/${studyRef}`;
            }
            if (!!studyRef && !!observationId) return `CentralMonitoring/${url}/ItemIndex/${studyRef}/${observationId}`;
            return null;
        case 'Actions':
            ({ level, observationId, reviewLogId } = args);
            if (!!studyRef && !!reviewLogId)
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/ItemIndex/${studyRef}/ForReviewLog/${reviewLogId}`;

            if (!!studyRef && !!observationId)
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/ItemIndex/${studyRef}/${observationId}`;
            if (!!studyRef) {
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/ItemIndex/${studyRef}`;
            }
            return '';
        case 'Observations':
            ({ level } = args);
            if (!!studyRef) return `CentralMonitoring/Observation/${observationTypeToUrl(level)}/ItemIndex/${studyRef}`;
            return null;
        case 'SmObservations':
            if (!!studyRef) return `${subjectObservationsUrl}/ItemIndex/${studyRef}`;
            return null;
        case 'SmActions':
            ({ observationId } = args);
            if (!!studyRef && !!observationId) return `${subjectActionsUrl}/ItemIndex/${studyRef}/${observationId}`;

            if (!!studyRef) return `${subjectActionsUrl}/ItemIndex/${studyRef}`;
            return null;
        case 'SmReviewLog':
            ({ observationId } = args);
            if (!!studyRef && !!observationId) return `${subjectReviewLogUrl}/ItemIndex/${studyRef}/${observationId}`;

            if (!!studyRef) return `${subjectReviewLogUrl}/ItemIndex/${studyRef}`;
            return null;
        default:
            return null;
    }
};
export const formExportUrl = (args: ApiGridParams) => {
    const { studyRef } = args;
    let level;
    let observationId;
    let isChild;
    let isScatterPlot;
    let url;
    let isRateGroup;
    let isHistory;
    let reviewLogId;
    let actionChild;
    let actionId;

    switch (args.grid) {
        case 'ReviewLog':
            ({ level, observationId, isChild, isScatterPlot, isRateGroup, isHistory, actionChild, actionId } = args);
            url = reviewLogTypeToUrl(level);
            const isParent = !isChild;
            const isRateGroupSet = isRateGroup ?? false;
            const queryParams = new URLSearchParams([['isHistory', (isHistory ?? false).toString()]]);

            // For the Control, Site and Study ( Actions Review Log child grid (only!) we don't use the observation id (get all review logs
            // that belong to the specified observation) but instead we use the review log id associated to the
            // action selected in the parent grid
            if (actionChild === true && !!studyRef && !!actionId) {
                let fullUrl = `CentralMonitoring/${url}/Grid/Export/${studyRef}/ActionChild/${actionId}`;
                if (level === ObservationType.Study || level === ObservationType.Site) fullUrl += `/${isScatterPlot}`;
                return fullUrl;
            }
            if (!!studyRef && !observationId) return `CentralMonitoring/${url}/Grid/Export/${studyRef}?${queryParams}`;
            if (!!studyRef && !!observationId && level === ObservationType.Study) {
                return (
                    `CentralMonitoring/${url}/Grid/Export/${studyRef}/${observationId}/${isParent}/${isScatterPlot}/${isRateGroupSet}` +
                    `?${queryParams}`
                );
            }
            return `CentralMonitoring/${url}/Grid/Export/${studyRef}/${observationId}/${isParent}/${isScatterPlot}?${queryParams}`;
        case 'Actions':
            ({ level, observationId, reviewLogId } = args);

            // For the Control Review Log Actions Child grid (only!) we don't use the observation id (get all actions
            // that belong to the specified observation) but instead we use the selected review log id for the parent
            // grid (get all actions for the selected review log).
            //
            // We put both values in observationId because it's easier but observationId isn't always an observationId.
            //
            // This will change in a later release so all action child grids show actions for the selected review log.
            if (!!studyRef && !!reviewLogId) {
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/Export/${studyRef}/ForReviewLog/${reviewLogId}`;
            }

            if (!!studyRef && !observationId) {
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/Export/${studyRef}`;
            }
            if (!!studyRef && !!observationId)
                return `CentralMonitoring/Actions/${observationTypeToUrl(level)}/Grid/Export/${studyRef}/${observationId}`;
            return null;
        case 'Observations':
            ({ level, observationId } = args);
            if (!!studyRef && !observationId) {
                return `CentralMonitoring/Observation/${observationTypeToUrl(level)}/Grid/Export/${studyRef}`;
            }
            if (!!studyRef && !!observationId)
                return `CentralMonitoring/Observation/${observationTypeToUrl(level)}/Grid/Export/${studyRef}/${observationId}`;
            return null;
        case 'DataTable': {
            const {
                dataset,
                datatable,
                subjectId,
                isSubjectLevelTable,
                comparisonDatasetId,
                dataViewRef,
                subjectsOnReviewId,
                subjectIsOnAReview,
            } = {
                ...args,
            };
            if (!!subjectId && !!studyRef && !!dataset && !!datatable) {
                return `SubjectMonitoring/DataListings/Export/${studyRef}/${dataset}/${datatable}/${subjectId}/${isSubjectLevelTable}`;
            }
            if ((!!studyRef && !!dataset && !datatable && !comparisonDatasetId) || datatable === '') {
                // On or off a specific review
                const smQueryParams = new URLSearchParams();
                if (subjectIsOnAReview != null && subjectsOnReviewId != null) {
                    smQueryParams.append('subjectIsOnAReview', `${subjectIsOnAReview}`);
                    smQueryParams.append('subjectsOnReviewId', `${subjectsOnReviewId}`);
                }
                return `SubjectMonitoring/OverviewGridData/Export/${studyRef}/${dataset}?${smQueryParams}`;
            }
            if ((!!studyRef && !!dataset && !datatable) || (datatable === '' && !!comparisonDatasetId)) {
                // On or off a specific review
                const smQueryParams = new URLSearchParams();
                if (subjectIsOnAReview != null && subjectsOnReviewId != null) {
                    smQueryParams.append('subjectIsOnAReview', `${subjectIsOnAReview}`);
                    smQueryParams.append('subjectsOnReviewId', `${subjectsOnReviewId}`);
                }
                return `SubjectMonitoring/OverviewGridData/Export/${studyRef}/${dataset}/${comparisonDatasetId}?${smQueryParams}`;
            }
            if (!!studyRef && !!dataset && !!datatable) {
                url = `CentralMonitoring/Grid/DataTable/Export/${studyRef}/${dataset}/${datatable}`;
                if (!!dataViewRef) url += `?dataViewRef=${dataViewRef}`;
                return url;
            }
            return null;
        }

        case 'SMDataTable': {
            const { subjectId, dataset, datatable, isSubjectLevelTable, reviewRecordId } = args;

            if (!!subjectId && !!studyRef && !!dataset && !!datatable) {
                const SMDataTableQueryParams = new URLSearchParams();
                if (reviewRecordId != null) {
                    SMDataTableQueryParams.append('reviewRecordId', `${reviewRecordId}`);
                }

                return (
                    `SubjectMonitoring/DataListings/Export/${studyRef}/${dataset}/${datatable}/${subjectId}/${isSubjectLevelTable}` +
                    `?${SMDataTableQueryParams}`
                );
            }

            return null;
        }

        case 'ControlsHubOoTGrid':
            url = `CentralMonitoring/ControlsHub/${args.studyRef}/exportOutOfToleranceGrid`;
            if (args.country) url += `?country=${args.country}`;
            return url;
        case 'ControlsHubPanelGrid':
            url = `CentralMonitoring/ControlsHub/${args.studyRef}/${args.datasetId}/exportPanelGridData/${args.panelRef}`;
            if (args.country) url += `?country=${args.country}`;
            return url;
        case 'SmObservations':
            ({ observationId } = args);
            if (!!studyRef && !observationId) {
                return `${subjectObservationsUrl}/Grid/Export/${studyRef}`;
            }
            if (!!studyRef && !!observationId)
                return `${subjectObservationsUrl}/Grid/Export/${studyRef}/${observationId}`;
            return null;
        case 'SmActions':
            ({ observationId } = args);
            if (!!studyRef && !observationId) {
                return `${subjectActionsUrl}/Grid/Export/${studyRef}`;
            }
            if (!!studyRef && !!observationId) return `${subjectActionsUrl}/Grid/Export/${studyRef}/${observationId}`;
            return null;
        case 'SmReviewLog':
            ({ observationId } = args);
            if (!!studyRef && !observationId) return `${subjectReviewLogUrl}/Grid/Export/${studyRef}`;
            if (!!studyRef && !!observationId) {
                return `${subjectReviewLogUrl}/Grid/Export/${studyRef}/${observationId}`;
            }
            return null;
        default:
            return null;
    }
};

// Not sure if this is going to be needed for non-Datatable but will follow the other methods style nonetheless
export const formConfigUrl = (gridParams: ApiGridParams) => {
    if (gridParams.grid === 'SubjectReviewActivityLog') {
        const { studyRef } = gridParams;
        const isParent = true;
        if (!!studyRef) {
            return `SubjectMonitoring/SubjectReviewActivityLogGrid/Definition/${studyRef}/${isParent}`;
        }
        return null;
    }
    if (gridParams.grid === 'DataTable') {
        const { studyRef, dataset, datatable, dataViewRef } = gridParams;
        if ((!!studyRef && !!dataset && !datatable) || datatable === '')
            return `SubjectMonitoring/OverviewGridDefinition/${studyRef}/${dataset}`;
        if (!!studyRef && !!dataset && !!datatable) {
            let url = `CentralMonitoring/Grid/Definition/DataTable/${studyRef}/${dataset}/${datatable}`;
            if (dataViewRef) url += `?dataViewRef=${dataViewRef}`;
            return url;
        }
        return null;
    }

    if (gridParams.grid === 'SMDataTable') {
        const { studyRef, dataset, datatable, reviewRecordId } = gridParams;

        const queryParams = new URLSearchParams();

        if (reviewRecordId != null) {
            queryParams.append('reviewRecordId', `${reviewRecordId}`);
        }

        return `SubjectMonitoring/DataListings/Definition/${studyRef}/${dataset}/${datatable}?${queryParams}`;
    }

    if (gridParams.grid === 'Observations') {
        const grid: GridType = 0;
        const { studyRef, level, isChild } = gridParams;
        const isParent = !isChild;
        return `CentralMonitoring/Grid/Definition/${grid}/${studyRef}/${observationTypeToUrl(level)}/${isParent}`;
    }
    if (gridParams.grid === 'Actions') {
        const grid: GridType = 1;
        const { studyRef, level, isChild } = gridParams;
        const isParent = !isChild;
        return `CentralMonitoring/Grid/Definition/${grid}/${studyRef}/${observationTypeToUrl(level)}/${isParent}`;
    }
    if (gridParams.grid === 'ReviewLog') {
        const grid: GridType = 2;
        const { studyRef, level, isChild, isScatterPlot, observationId, isRateGroup } = gridParams;
        const isParent = !isChild;
        const isRateGroupSet = isRateGroup ?? false;
        // Control Review logs aren't scatter plots but need the observation id
        if (
            isScatterPlot ||
            (level === ObservationType.Controls && observationId !== null && observationId !== undefined)
        ) {
            // eslint-disable-next-line max-len
            return `CentralMonitoring/Grid/Definition/${grid}/${studyRef}/${observationTypeToUrl(level)}/${isParent}/${isScatterPlot}/${isRateGroupSet}/${observationId}`;
        }
        return `CentralMonitoring/Grid/Definition/${grid}/${studyRef}/${observationTypeToUrl(level)}/${isParent}`;
    }
    if (gridParams.grid === 'ControlsHubOoTGrid')
        return `CentralMonitoring/ControlsHub/${gridParams.studyRef}/panelGridDefinitions`;
    if (gridParams.grid === 'ControlsHubPanelGrid')
        return `CentralMonitoring/ControlsHub/${gridParams.studyRef}/${gridParams.datasetId}/panelGridDefinitions/${gridParams.panelRef}`;
    if (gridParams.grid === 'SmObservations') {
        const { studyRef, isChild } = gridParams;
        const isParent = !isChild;
        return `${subjectObservationsUrl}/Grid/Definition/${studyRef}/${isParent}`;
    }
    if (gridParams.grid === 'SmReviewLog') {
        const { studyRef, isChild } = gridParams;
        const isParent = !isChild;
        return `${subjectReviewLogUrl}/Grid/Definition/${studyRef}/${isParent}`;
    }
    if (gridParams.grid === 'SmActions') {
        const { studyRef, isChild } = gridParams;
        const isParent = !isChild;
        return `${subjectActionsUrl}/Grid/Definition/${studyRef}/${isParent}`;
    }
    return null;
};
